<template>
  <div class="tb-form-group" v-on:keyup.stop="">
    <label class="tb-label">
      Content
    </label>

    <template v-if="editable">
      <text-editor
        ref="editor"
        :editing="true"
        :editable="editable"
        :element="element"
        :text.sync="text"
        data-test="graphics-editor-text-editor"
        @emoji="appendEmoji"
      />

      <p
        v-if="element.limitCharacters"
        class="text-right"
        data-test="graphics-editor-text-characterCount"
      >
        {{ characterCount }} / {{ element.maxCharacterLength }}
      </p>
    </template>

    <div v-else class="text-em" data-test="graphics-editor-textMapping">
      {{ mappingLabel }}
    </div>

    <label class="tb-label margin-top-md">
      Text
    </label>

    <!-- Font Controls  -->
    <div class="flex items-center text-style-controls">
      <!-- Font Size -->
      <div class="text-style-controls__field">
        <label>
          Font<br>Size
        </label>

        <input
          :title="element.fitText ? 'Disabled when autosize enabled' : 'Font Size'"
          type="number"
          :disabled="element.fitText"
          v-model="fontSize"
          data-test="graphics-editor-text-fontSize"
        >
      </div>

      <!-- Letter Spacing -->
      <div class="text-style-controls__field">
        <label>
          Letter<br>Spacing
        </label>

        <input
          title="Letter Spacing"
          type="number"
          step="0.1"
          v-model="letterSpacing"
          data-test="graphics-editor-text-letterSpacing"
        >
      </div>

      <!-- Line Spacing (lineHeight) -->
      <div class="text-style-controls__field" v-if="!isMarqueeText">
        <label>
          Line<br>Spacing
        </label>

        <input
          title="Line Spacing"
          type="number"
          step="0.1"
          v-model="lineHeight"
          data-test="graphics-editor-text-lineHeight"
        >
      </div>
    </div>

    <!-- Text Color Picker  -->
    <label class="tb-label margin-top-md">
      Style
    </label>

    <div class="text-style-controls">
      <colorpicker
        :color="color"
        data-test="graphics-editor-text-color"
        @change="color = $event"
      />

      <!-- Toggle Buttons for Styling -->
      <div class="flex items-center margin-left-md">
        <button
          title="Bold"
          class="tb-btn tb-btn-text"
          :class="{ active: fontWeight === 'bold' }"
          data-entity="producer-toggleTextBold"
          data-test="graphics-editor-text-bold"
          @click="fontWeight = fontWeight === 'bold' ? 'normal' : 'bold'"
        >
          <span class="fas fa-bold" />
        </button>

        <button
          title="Italic"
          class="tb-btn tb-btn-text"
          :class="{ active: fontStyle === 'italic' }"
          data-entity="producer-toggleTextStyle"
          data-test="graphics-editor-text-italic"
          @click="fontStyle = fontStyle === 'italic' ? 'normal' : 'italic'"
        >
          <span class="fas fa-italic" />
        </button>

        <button
          title="Underline"
          class="tb-btn tb-btn-text"
          :class="{ active: textDecoration === 'underline' }"
          data-entity="producer-toggleUnderline"
          data-test="graphics-editor-text-underline"
          @click="textDecoration = textDecoration === 'underline' ? 'none' : 'underline'"
        >
          <span class="fas fa-underline" />
        </button>

        <button
          title="Strikethrough"
          class="tb-btn tb-btn-text"
          :class="{ active: textDecoration === 'line-through' }"
          data-entity="producer-toggleStrikethrough"
          data-test="graphics-editor-text-strikethrough"
          @click="textDecoration = textDecoration === 'line-through' ? 'none' : 'line-through'"
        >
          <span class="fas fa-strikethrough" />
        </button>

        <button
          title="Uppercase"
          class="tb-btn tb-btn-text"
          :class="{ active: textTransform === 'uppercase' }"
          data-entity="producer-toggleUppercase"
          data-test="graphics-editor-text-uppercase"
          @click="textTransform = textTransform === 'uppercase' ? 'none' : 'uppercase'"
        >
          <span class="fas fa-font" />
        </button>
      </div>
    </div>

    <label class="tb-label margin-top-md" v-if="!isMarqueeText">
      Alignment
    </label>

    <div class="flex items-center" v-if="!isMarqueeText">
      <!-- Toggle Buttons for Alignment -->
      <div class="flex items-center text-style-controls margin-right-md">
        <button
          title="Left Aligned"
          class="tb-btn tb-btn-text"
          :class="{ active: textAlign === 'left' }"
          data-entity="producer-toggleLeftAlign"
          data-test="graphics-editor-text-align-left"
          @click="textAlign = 'left'"
        >
          <span class="fas fa-align-left" />
        </button>

        <button
          title="Center Aligned"
          class="tb-btn tb-btn-text"
          :class="{ active: textAlign === 'center' }"
          data-entity="producer-toggleCenterAlign"
          data-test="graphics-editor-text-align-center"
          @click="textAlign = 'center'"
        >
          <span class="fas fa-align-center" />
        </button>

        <button
          title="Right Aligned"
          class="tb-btn tb-btn-text"
          :class="{ active: textAlign === 'right' }"
          data-entity="producer-toggleRightAlign"
          data-test="graphics-editor-text-align-right"
          @click="textAlign = 'right'"
        >
          <span class="fas fa-align-right" />
        </button>

        <button
          title="Justified"
          class="tb-btn tb-btn-text"
          :class="{ active: textAlign === 'justify' }"
          data-entity="producer-toggleJustifyAlign"
          data-test="graphics-editor-text-align-justify"
          @click="textAlign = 'justify'"
        >
          <span class="fas fa-align-justify" />
        </button>
      </div>

      <!-- Toggle Buttons for Vertical Alignment -->
      <div class="flex items-center text-style-controls">
        <button
          title="Top"
          class="tb-btn tb-btn-text"
          :class="{ active: verticalAlign === 'top' }"
          data-entity="producer-toggleTopAlign"
          data-test="graphics-editor-text-verticalAlign-top"
          @click="verticalAlign = 'top'"
        >
          <span class="fas fa-font" />
          <span class="fas fa-long-arrow-alt-up" />
        </button>

        <button
          title="Middle"
          class="tb-btn tb-btn-text"
          :class="{ active: verticalAlign === 'middle' }"
          data-entity="producer-toggleMiddleAlign"
          data-test="graphics-editor-text-verticalAlign-middle"
          @click="verticalAlign = 'middle'"
        >
          <span class="fas fa-font" />
          <span class="fas fa-arrows-alt-v" />
        </button>

        <button
          title="Bottom"
          class="tb-btn tb-btn-text"
          :class="{ active: verticalAlign === 'bottom' }"
          data-entity="producer-toggleBottomAlign"
          data-test="graphics-editor-text-verticalAlign-bottom"
          @click="verticalAlign = 'bottom'"
        >
          <span class="fas fa-font" />
          <span class="fas fa-long-arrow-alt-down" />
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="js">
import { get } from 'lodash-es';

import TextEditor from '../../../TextEditor.vue';

export default {
  props: ['element', 'graphic_fields'],

  components: {
    TextEditor,
  },

  computed: {
    isMarqueeText() {
      return this.element.type === 'marquee-text';
    },
    text: {
      get() {
        const fields = this.graphic_fields?.[this.element.id] || {};
        return fields.text || this.element.text;
      },

      set(text) {
        this.updateField('text', text);
      },
    },

    color: {
      get() {
        const fields = this.graphic_fields?.[this.element.id] || {};
        return fields.styles?.color || this.element.styles?.color;
      },

      set(color) {
        this.updateField('styles.color', color);
      },
    },

    fontWeight: {
      get() {
        const fields = this.graphic_fields?.[this.element.id] || {};
        return fields?.styles?.fontWeight || this.element.styles?.fontWeight || 'normal';
      },

      set(fontWeight) {
        this.updateField('styles.fontWeight', fontWeight);
      },
    },

    fontStyle: {
      get() {
        const fields = this.graphic_fields?.[this.element.id] || {};
        return fields?.styles?.fontStyle || this.element.styles?.fontStyle || 'normal';
      },

      set(fontStyle) {
        this.updateField('styles.fontStyle', fontStyle);
      },
    },

    textDecoration: {
      get() {
        const fields = this.graphic_fields?.[this.element.id] || {};
        return fields?.styles?.textDecoration || this.element.styles?.textDecoration || 'none';
      },

      set(textDecoration) {
        this.updateField('styles.textDecoration', textDecoration);
      },
    },

    textTransform: {
      get() {
        const fields = this.graphic_fields?.[this.element.id] || {};
        return fields?.styles?.textTransform || this.element.styles?.textTransform || 'none';
      },

      set(textTransform) {
        this.updateField('styles.textTransform', textTransform);
      },
    },

    textAlign: {
      get() {
        const fields = this.graphic_fields?.[this.element.id] || {};
        return fields?.styles?.textAlign || this.element.styles?.textAlign || 'left';
      },

      set(textAlign) {
        this.updateField('styles.textAlign', textAlign);
      },
    },

    verticalAlign: {
      get() {
        const fields = this.graphic_fields?.[this.element.id] || {};
        return fields?.verticalAlign || this.element.verticalAlign || 'top';
      },

      set(verticalAlign) {
        this.updateField('verticalAlign', verticalAlign);
      },
    },

    fontSize: {
      get() {
        const fields = this.graphic_fields?.[this.element.id] || {};
        return fields?.styles?.fontSize || this.element.styles?.fontSize;
      },

      set(fontSize) {
        this.updateField('styles.fontSize', Math.round(fontSize));
      },
    },

    letterSpacing: {
      get() {
        const fields = this.graphic_fields?.[this.element.id] || {};
        return fields?.styles?.letterSpacing || this.element.styles?.letterSpacing;
      },

      set(letterSpacing) {
        this.updateField('styles.letterSpacing', letterSpacing);
      },
    },

    lineHeight: {
      get() {
        const fields = this.graphic_fields?.[this.element.id] || {};
        return fields?.styles?.lineHeight || this.element.styles?.lineHeight;
      },

      set(lineHeight) {
        this.updateField('styles.lineHeight', lineHeight);
      },
    },

    editable() {
      // sheets, only a single property can be mapped (which is text)
      if (this.element.data?.columnIdx || this.element.data?.columnIdx === 0) {
        return !get(this.element, 'data.provider');
      }

      // only disable if text property is mapped
      return get(this.element, 'data.dataKeyMap.text') === undefined;
    },

    mappingLabel() {
      if (this.editable) {
        return '';
      }

      if (this.element.data?.columnIdx || this.element.data?.columnIdx === 0) {
        return `Field mapped to column (${this.element.data.columnIdx + 1}) of sheet data`;
      }

      if (this.element.data?.dataKeyMap?.text !== undefined) {
        return `Field mapped to [${this.element.data.dataKeyMap.text}]`;
      }

      return 'Field mapped to data';
    },
  },

  methods: {
    appendEmoji(emoji) {
      // Removing traing <br> before appending.
      // This helps sometimes, but often the emojis will still
      // be placed on a newline because of the way the text is set by the contenteditable (fun HTML).
      this.text = this.text.replace(/<br>$/, '') + emoji;
    },

    updateField(field, value) {
      this.$emit('value', { field, value });
    },
  },
};
</script>

<style lang="scss">
.sp-replacer {
  height: 32px;

  .sp-preview {
    height: 24px;
  }
}

.text-style-controls {
  display: flex;
  align-items: center;

  .text-style-controls__field {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column-reverse;
    margin-right: 1rem;

    input {
      width: 6rem;
      appearance: none;
      -webkit-appearance: none;
      height: 32px;
      line-height: 32px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      padding: 0 0.5rem;
      background: #333333;
      color: #E1E1E1;
      border-radius: 2px;

      &:disabled {
        cursor: not-allowed;
      }
    }

    label {
      text-align: center;
      color: rgba(255, 255, 255, 0.5);
      margin-top: 0.25rem;
      font-size: 10px;
    }
  }

  .tb-btn {
    border: 1px solid rgba(255, 255, 255, 0.1);
    min-width: 32px;
    height: 32px;
    margin-left: 0;
    margin-right: 0;
    border-radius: 0;

    &:not(:last-child) {
      border-right: 0;
    }

    &:first-child {
      border-radius: 2px 0 0 2px;
    }

    &:last-child {
      border-radius: 0 2px 2px 0;
    }
  }
}
</style>
